import Utils, { UtilsValidators } from 'src/app/helpers/utils';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';

import { SignupService } from 'src/app/services/signup/signup.service';
import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import DDIOptions from 'src/app/data/ddi';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { LoadingService } from 'src/app/services/Loading/loading.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-signup-unified-personal',
  templateUrl: './signup-unified-personal.component.html',
  styleUrls: ['./signup-unified-personal.component.scss'],
})
export class SignupUnifiedPersonalComponent implements OnInit, OnDestroy {
  @Input() userRole: string;
  @Input() disabled: boolean = false;
  @Input() inviteStatus: string | null = null;
  @Input() inviteUsed: boolean = false;
  @Input() inviteCanceled: boolean = false;
  @Input() inviteExpired: boolean = false;

  DDIOptions = DDIOptions;

  static fullNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value: string = control.value.trim();

    // Divide o nome em palavras e remove strings vazias
    const words = value.split(/\s+/).filter((word) => word.length > 0);

    // Verifica se tem pelo menos 2 palavras
    if (words.length < 2) {
      return { minNames: true };
    }

    // Verifica se há abreviações (palavras com apenas uma letra ou palavras com ponto)
    for (const word of words) {
      // Verifica se é uma palavra com apenas uma letra (abreviação)
      if (word.length === 1) {
        return { abbreviation: true };
      }

      // Verifica se a palavra contém ponto (abreviação como "S.", "J.", etc.)
      if (word.includes('.')) {
        return { abbreviation: true };
      }
    }

    return null;
  }

  static normalizeFullName(value: string | null): string | null {
    if (!value) {
      return value;
    }
    // Remove espaços múltiplos e espaços no início/fim
    return value.trim().replace(/\s+/g, ' ');
  }

  static normalizeEmail(value: string | null): string | null {
    if (!value) {
      return value;
    }
    // Converte para minúsculas e remove espaços
    return value.trim().toLowerCase();
  }

  static phoneNumberValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const phoneValue: string = control.value;
    const phoneNumbers = Utils.onlyNumbers(phoneValue);

    // Verifica se o número tem tamanho válido (10 ou 11 dígitos)
    if (phoneNumbers.length !== 10 && phoneNumbers.length !== 11) {
      return { invalidPhoneLength: true };
    }

    // Verifica se o DDI é 55 (Brasil)
    const parent = control.parent;
    if (parent) {
      const ddiControl = parent.get('phone_ddi');
      if (ddiControl && ddiControl.value === '55') {
        // Se o DDI é 55, verifica se o número começa com 55
        if (phoneNumbers.length >= 4) {
          const firstTwoDigits = phoneNumbers.substring(0, 2);
          if (firstTwoDigits === '55') {
            // Se começa com 55, verifica se tem outro DDD depois (posições 2-3)
            // DDDs válidos no Brasil são de 11 a 99 (exceto alguns específicos)
            const nextTwoDigits = phoneNumbers.substring(2, 4);
            // Se os próximos dois dígitos formam um DDD válido (11-99), é erro
            // Porque 55 seria o DDI, não o DDD, então não deveria ter outro DDD depois
            const dddPattern = /^(1[1-9]|[2-9][0-9])$/;
            if (dddPattern.test(nextTwoDigits)) {
              return { invalidDDI: true };
            }
            // Se 55 é o DDD (não tem outro DDD depois), é válido - não retorna erro
          }
        }
      }
    }

    // Verifica números inválidos: todos iguais (000000, 11111, etc.)
    if (/^(\d)\1+$/.test(phoneNumbers)) {
      return { invalidPhonePattern: true };
    }

    // Verifica se começa com 13- (formato inválido)
    if (phoneValue.includes('13-')) {
      return { invalidPhonePattern: true };
    }

    // Verifica sequências inválidas como 1235555555
    if (/^123\d+$/.test(phoneNumbers)) {
      return { invalidPhonePattern: true };
    }

    // Verifica se o primeiro dígito é 0 (inválido para DDD)
    if (phoneNumbers[0] === '0') {
      return { invalidPhonePattern: true };
    }

    // Verifica se o terceiro dígito é 0 (inválido para alguns DDDs)
    if (phoneNumbers.length >= 3 && phoneNumbers[2] === '0') {
      return { invalidPhonePattern: true };
    }

    return null;
  }

  form = this.formBuilder.group({
    full_name: [
      null,
      [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(60),
        Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/),
        SignupUnifiedPersonalComponent.fullNameValidator,
      ],
    ],
    nacionality: ['23', [Validators.required]],
    document: [null, [Validators.required, UtilsValidators.cpf]],
    document_type: [null, []],
    document_name: [null, []],
    email: [
      null,
      [Validators.required, Validators.email, UtilsValidators.emailValidator],
    ],
    phone_ddi: ['55', [Validators.required]],
    fund: [null, [Validators.required]],
    agent: [null, []],
    phone_number: [
      null,
      [
        Validators.required,
        UtilsValidators.telefone,
        SignupUnifiedPersonalComponent.phoneNumberValidator,
      ],
    ],

    terms: [false, [Validators.requiredTrue]],
  });

  nacionalities: SelectItem[] = [{ label: 'Brasileiro', value: '1' }];

  documents: SelectItem[] = [
    { label: 'Passaporte', value: '33' },
    { label: 'Número de Identificação Fiscal (NIF)', value: 'nif' },
    { label: 'Outro', value: 'other' },
  ];

  ddi: SelectItem[] = [{ label: '+55', value: '+55' }];

  name: string = '';
  isPF: boolean = false;
  role: string = '';
  typeDisplay: string = ''; // Tipo do cadastro (ex: Cedente, Cotista)

  haveToken: boolean = true;
  loading: boolean = true;

  haveName: boolean = false;
  haveFund: boolean = false;
  haveRole: boolean = false;
  haveBasicPFInfo: boolean = false;
  haveEmail: boolean = false;
  haveDocument: boolean = false;

  fundOptions: SelectItem[] = [];
  agentOptions: SelectItem[] = [];

  // Propriedades para login quando há new_paper
  showLoginForm: boolean = false;
  loginForm = this.formBuilder.group({
    username: [null, [Validators.required, Utils.validateCpfCnpj]],
  });

  loginPasswordForm = this.formBuilder.group({
    password: [null, [Validators.required, Validators.minLength(8)]],
  });

  loginLoading = false;
  loginTypeDisplay: string = '';
  loginUserName: string = '';
  loginFundName: string = '';
  loginInvitePersonType: string | null = null; // PF ou PJ do convite
  loginInviteRole: string | null = null; // papel do convite (assignor, shareholder, provider)

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private maskPipe: MaskPipe,
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService,
    private toast: ToastService,
    public loadingService: LoadingService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  ngOnDestroy(): void {
    this.signupService.fillFormEvent.unsubscribe();
  }

  async recheckLoginCondition() {
    const queryParams = this.route.snapshot.queryParams;
    const registerUuidFromUrl = queryParams['register_uuid'];
    const newPaper = queryParams['new_paper'];
    const noAccess = queryParams['no_access'] === 'true' || queryParams['no_access'] === 'True';
    const token = this.authService.getToken();

    // Se há new_paper + no_access=true, NÃO mostrar login (mostrar formulário normal)
    // Se há new_paper sem no_access, mostrar login (usuário já tem acesso)
    if (newPaper && !token && !noAccess) {
      this.showLoginForm = true;
      await this.initLoginComponent(newPaper, registerUuidFromUrl);
      this.loading = false;
      this.cdr.detectChanges();
      
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async ngOnInit(): Promise<void> {
    console.log('[SIGNUP-UNIFIED-PERSONAL] ngOnInit iniciado');

    // Verificar se há register_uuid na URL e se o usuário já está logado
    // Se o UUID da URL for o mesmo do usuário ativo, redirecionar direto para o cadastro
    const queryParams = this.route.snapshot.queryParams;
    const registerUuidFromUrl = queryParams['register_uuid'];
    const newPaper = queryParams['new_paper'];
    const noAccess = queryParams['no_access'] === 'true' || queryParams['no_access'] === 'True';

    console.log('[SIGNUP-UNIFIED-PERSONAL] queryParams:', queryParams);
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] registerUuidFromUrl:',
      registerUuidFromUrl
    );
    console.log('[SIGNUP-UNIFIED-PERSONAL] newPaper:', newPaper);
    console.log('[SIGNUP-UNIFIED-PERSONAL] noAccess:', noAccess);

    // Verificar se o usuário está logado
    const token = this.authService.getToken();
    const user = this.authService.getUser();

    console.log('[SIGNUP-UNIFIED-PERSONAL] Token existe:', !!token);
    console.log('[SIGNUP-UNIFIED-PERSONAL] User existe:', !!user);
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] showLoginForm antes:',
      this.showLoginForm
    );

    // Se há new_paper + no_access=true, NÃO mostrar login (mostrar formulário normal)
    // Se há new_paper sem no_access, mostrar login (usuário já tem acesso)
    if (newPaper && !token && !noAccess) {
      console.log(
        '[SIGNUP-UNIFIED-PERSONAL] Condição atendida: newPaper && !token'
      );
      console.log('[SIGNUP-UNIFIED-PERSONAL] Ativando showLoginForm');
      this.showLoginForm = true;
      console.log(
        '[SIGNUP-UNIFIED-PERSONAL] showLoginForm após ativação:',
        this.showLoginForm
      );
      await this.initLoginComponent(newPaper, registerUuidFromUrl);
      this.loading = false;
      console.log('[SIGNUP-UNIFIED-PERSONAL] initLoginComponent concluído');

      // Forçar detecção de mudanças após inicializar o componente de login
      // Isso garante que o template seja atualizado mesmo no hot reload
      this.cdr.detectChanges();

      // Forçar detecção de mudanças novamente após um pequeno delay
      // para garantir que o Angular processe todas as mudanças
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);

      return;
    } else {
      console.log(
        '[SIGNUP-UNIFIED-PERSONAL] Condição NÃO atendida - newPaper:',
        newPaper,
        'token:',
        !!token
      );
    }

    if (token && registerUuidFromUrl && newPaper) {
      const user = this.authService.getUser();
      if (user?.active_register?.register?.id) {
        const activeRegisterId = user.active_register.register.id.toString();
        const urlRegisterId = registerUuidFromUrl.toString();

        // Se os IDs são iguais, redirecionar para o cadastro
        if (activeRegisterId === urlRegisterId) {
          // Buscar dados do invite para determinar papel e tipo
          const routeToken = this.route.snapshot.params.token;
          if (routeToken) {
            try {
              const inviteRes = await this.api.get({
                route: `api/v3/registration/invite/public/`,
                params: { token: routeToken },
              });

              if (inviteRes?.data) {
                const inviteData = inviteRes.data.data || inviteRes.data;

                // Verificar se o convite já foi usado
                if (
                  inviteRes.data.status === 'used' ||
                  inviteData.status === 'used'
                ) {
                  // Convite já foi usado - verificar se o papel já foi adicionado
                  try {
                    // Buscar dados atualizados do register
                    const registerResponse = await this.api.get<any>({
                      route: `api/registration/register/${activeRegisterId}`,
                      token: true,
                    });

                    const register = registerResponse?.data;
                    const targetRoleApplicable =
                      inviteRes.data.type || newPaper;

                    // Verificar se o papel já existe no register
                    const roleAlreadyExists =
                      register?.roles &&
                      Array.isArray(register.roles) &&
                      register.roles.some(
                        (r: any) =>
                          r &&
                          (r.applicable === targetRoleApplicable ||
                            r.id === inviteRes.data.invited_current_role?.id)
                      );

                    if (roleAlreadyExists) {
                      // Papel já foi adicionado - redirecionar para página de detalhes
                      this.toast.show(
                        'info',
                        'Papel já adicionado',
                        'Este papel já foi adicionado ao seu cadastro.'
                      );
                      this.router.navigate([
                        `/app/details/${targetRoleApplicable}/${activeRegisterId}`,
                      ]);
                      return;
                    }
                  } catch (error) {
                    console.error('Erro ao verificar papel existente:', error);
                    // Se não conseguir verificar, continua o fluxo normal
                  }
                }

                // Determinar o papel (role)
                const role =
                  inviteRes.data.type ||
                  newPaper ||
                  user.active_register.register.role?.applicable;

                // Determinar o tipo (PF ou PJ)
                let personType = 'pf';
                if (inviteData.person_type) {
                  personType = inviteData.person_type.toLowerCase();
                } else if (inviteData.document) {
                  const docLength = inviteData.document.replace(
                    /\D/g,
                    ''
                  ).length;
                  personType = docLength === 11 ? 'pf' : 'pj';
                } else if (user.active_register.register.type) {
                  personType = user.active_register.register.type.toLowerCase();
                }

                // Verificar se o papel já existe no register antes de adicionar
                let shouldAddRole = true;
                try {
                  // Buscar dados atualizados do register
                  const registerResponse = await this.api.get<any>({
                    route: `api/registration/register/${activeRegisterId}`,
                    token: true,
                  });

                  const register = registerResponse?.data;
                  const targetRoleApplicable = role;

                  // Verificar se o papel já existe no register
                  const roleAlreadyExists =
                    register?.roles &&
                    Array.isArray(register.roles) &&
                    register.roles.some(
                      (r: any) =>
                        r &&
                        (r.applicable === targetRoleApplicable ||
                          (inviteRes.data.invited_current_role &&
                            r.id === inviteRes.data.invited_current_role.id))
                    );

                  if (roleAlreadyExists) {
                    // Papel já foi adicionado - redirecionar para página de detalhes
                    this.toast.show(
                      'info',
                      'Papel já adicionado',
                      'Este papel já foi adicionado ao seu cadastro.'
                    );
                    this.router.navigate([
                      `/app/details/${targetRoleApplicable}/${activeRegisterId}`,
                    ]);
                    return;
                  }
                } catch (error) {
                  console.error('Erro ao verificar papel existente:', error);
                  // Se não conseguir verificar, continua o fluxo normal
                }

                // Fazer PATCH no register-step para definir como 'register' e 'general'
                // Isso evita que o componente redirecione para dashboard quando fizer o GET
                try {
                  if (personType === 'pf') {
                    await this.signupService.updateUserStepPF(
                      'register',
                      'general',
                      activeRegisterId
                    );
                  } else {
                    await this.signupService.updateUserStepCompany(
                      'register',
                      'general',
                      activeRegisterId
                    );
                  }
                } catch (error) {
                  console.error('Erro ao atualizar register-step:', error);
                  // Não bloqueia o redirecionamento mesmo se o PATCH falhar
                }

                // Marcar o convite como usado antes de redirecionar (só se ainda não estiver usado)
                if (
                  inviteRes.data.status !== 'used' &&
                  inviteData.status !== 'used'
                ) {
                  try {
                    await this.api.patch({
                      route: `api/v3/registration/invite/public/`,

                      body: {
                        used: true,
                        status: 'used',
                        token: routeToken,
                      },
                      token: true,
                    });
                  } catch (error) {
                    console.error('Erro ao marcar convite como usado:', error);
                    // Não bloqueia o redirecionamento mesmo se o PATCH falhar
                  }
                }

                // Adicionar role ao register se houver invited_current_role e o papel ainda não existir
                if (inviteRes.data.invited_current_role && shouldAddRole) {
                  try {
                    await this.api.patch({
                      route: `api/v3/registration/registers/`,
                      body: {
                        register_uuid: activeRegisterId,
                        role_draft: inviteRes.data.invited_current_role,
                      },
                      token: true,
                    });
                  } catch (error) {
                    console.error('Erro ao adicionar role ao register:', error);
                    // Não bloqueia o redirecionamento mesmo se o PATCH falhar
                  }

                  const draft = inviteRes.data.invited_current_role as {
                    id?: number;
                  };
                  const papersRoleId =
                    draft && typeof draft === 'object' && draft.id != null
                      ? Number(draft.id)
                      : null;
                  if (papersRoleId != null && !Number.isNaN(papersRoleId)) {
                    try {
                      await this.api.post({
                        route: `api/v2/registration/registers/${activeRegisterId}/papers/`,
                        token: true,
                        body: { role_id: papersRoleId },
                      });
                    } catch (papersErr) {
                      console.error(
                        'Erro ao registrar papers após role_draft:',
                        papersErr,
                      );
                      this.toast.show(
                        'warning',
                        'Atenção',
                        'Fluxo iniciado, mas houve falha ao registrar o papel. Tente novamente ou contate o suporte.',
                      );
                    }
                  }
                }

                // Redirecionar para o cadastro
                this.router.navigate([
                  `/signup/${role}/${personType}/${activeRegisterId}`,
                ]);
                return;
              }
            } catch (error) {
              console.error('Erro ao buscar dados do invite:', error);
            }
          }
        }
      }
    }

    this.haveToken = this.route.snapshot.params.token ? true : false;

    // Define nacionalidade como Brasil (23) por padrão e sempre
    this.form.controls.nacionality.setValue('23');

    // Carrega fundos e agentes primeiro
    await this.getFunds();

    if (this.userRole === 'provider') {
      await this.getAgents();
      this.form.controls.agent.setValidators([Validators.required]);
      this.form.controls.fund.setValidators([]);
      this.form.controls.agent.updateValueAndValidity();
      this.form.controls.fund.updateValueAndValidity();
    }

    // Busca dados do invite se houver token
    let inviteData: any = null;
    if (this.haveToken && this.route.snapshot.params.token) {
      const token = this.route.snapshot.params.token;
      try {
        const res = await this.api.get({
          route: `api/v3/registration/invite/public/`,
          params: { token },
        });

        if (res && res.data) {
          inviteData = res.data.data || res.data;

          // Captura o type_display do convite
          if (res.data.type_display) {
            this.typeDisplay = res.data.type_display;
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados do invite:', error);
      }
    }

    // Carrega dados salvos do sessionStorage ou do signupService
    const savedDataFromStorage = this.storageService.getSession(
      'signup_basic_personal',
      true
    );
    const savedDataFromService =
      this.signupService.getBasicFormData('personal');
    const savedData = savedDataFromStorage || savedDataFromService;

    // Aplica dados do invite primeiro (sempre que houver invite)
    // Isso garante que os dados da API sejam aplicados
    if (inviteData) {
      // Processar dados do invite
      const inviteFormData: any = {};

      // Verificar tipo de documento
      let isPersonaFisica = false;
      if (inviteData.document) {
        const docNumbers = inviteData.document.replace(/\D/g, '');
        isPersonaFisica = Utils.isCpfDocument(docNumbers);
        this.isPF = isPersonaFisica;
        this.haveBasicPFInfo = isPersonaFisica;
      }

      // Nome
      if (inviteData.name) {
        this.name = inviteData.name;
        this.haveName = true;

        // Preencher full_name APENAS se for Pessoa Física
        if (isPersonaFisica) {
          inviteFormData.full_name =
            SignupUnifiedPersonalComponent.normalizeFullName(inviteData.name);
        }
      }

      // Email
      if (inviteData.email) {
        this.haveEmail = true;
        inviteFormData.email = SignupUnifiedPersonalComponent.normalizeEmail(
          inviteData.email
        );
      }

      // Documento (apenas PF)
      if (inviteData.document && isPersonaFisica) {
        this.haveDocument = true;
        inviteFormData.document = inviteData.document;
      }

      // Fundo
      if (inviteData.fund) {
        this.haveFund = true;
        inviteFormData.fund = inviteData.fund;
      }

      // Agente (se for provider)
      if (
        this.userRole === 'provider' &&
        inviteData.agent &&
        Array.isArray(inviteData.agent) &&
        inviteData.agent.length > 0
      ) {
        const roleArr = inviteData.agent
          .map((item: any) => item.name.toLowerCase())
          .join(', ');

        const pos = roleArr.lastIndexOf(', ');
        const phrase =
          pos > 0
            ? `${roleArr.substring(0, pos)} e${roleArr.substring(pos + 1)}`
            : roleArr;

        this.role = phrase;
        this.haveRole = true;
        inviteFormData.agent = inviteData.agent.map((item: any) => item.id);
      }

      // Aplica todos os dados do invite de uma vez
      this.form.patchValue(inviteFormData, { emitEvent: false });

      // Desabilita documento se veio do invite (apenas CPF)
      if (inviteData.document && isPersonaFisica) {
        this.form.controls.document.disable();
      }

      // Salva dados do invite no storage e service
      const rawValues = this.form.getRawValue();
      const normalizedValues = {
        ...rawValues,
        full_name: rawValues.full_name
          ? SignupUnifiedPersonalComponent.normalizeFullName(
              rawValues.full_name
            )
          : rawValues.full_name,
        email: rawValues.email
          ? SignupUnifiedPersonalComponent.normalizeEmail(rawValues.email)
          : rawValues.email,
      };
      this.signupService.setBasicFormData('personal', normalizedValues);
      this.storageService.setSession(
        'signup_basic_personal',
        normalizedValues,
        true
      );
    }

    // Aplica dados salvos POR ÚLTIMO para garantir que ao voltar, os dados digitados sejam mantidos
    // Isso sobrescreve os dados do invite apenas nos campos que o usuário preencheu (telefone, termos, etc.)
    // Mas preserva os dados do invite (document, email, fund, agent, full_name) que vêm da API
    if (savedData) {
      // Merge inteligente: preserva dados do invite e sobrescreve apenas campos do usuário
      const currentFormData = this.form.getRawValue();
      const mergedData = {
        ...currentFormData, // Dados atuais (do invite) - preserva document, email, fund, agent, full_name
        // Sobrescreve apenas campos que o usuário preencheu
        phone_ddi:
          savedData.phone_ddi !== undefined
            ? savedData.phone_ddi
            : currentFormData.phone_ddi,
        phone_number:
          savedData.phone_number !== undefined
            ? savedData.phone_number
            : currentFormData.phone_number,
        terms:
          savedData.terms === true
            ? true
            : savedData.terms === false
            ? false
            : currentFormData.terms || false,
      };

      // Aplica os dados salvos primeiro sem emitir eventos
      this.form.patchValue(mergedData, { emitEvent: false });

      // Força a atualização do controle terms de forma explícita para garantir que o checkbox seja atualizado visualmente
      // O problema é que o componente radio precisa receber o valor via writeValue, então precisamos forçar isso
      if (savedData.terms !== undefined) {
        // Primeiro reseta para null para forçar a atualização, depois aplica o valor correto
        this.form.controls.terms.setValue(null, { emitEvent: false });
        // Usa setTimeout para garantir que o componente seja atualizado no próximo ciclo
        setTimeout(() => {
          this.form.controls.terms.setValue(savedData.terms === true, {
            emitEvent: true,
          });
          // Força a atualização do componente novamente
          this.form.controls.terms.updateValueAndValidity({ emitEvent: false });
          // Força a detecção de mudanças para atualizar o componente visualmente
          this.cdr.detectChanges();
        }, 10);
      }

      // Atualiza o status do formulário após carregar os dados salvos
      setTimeout(() => {
        this.form.updateValueAndValidity();
        const formStatus = this.form.status === 'VALID';
        this.signupService.setBasicFormStatus('personal', formStatus);
      }, 150);

      if (savedDataFromStorage) {
        this.signupService.setBasicFormData('personal', mergedData);
      }
    }

    // Configura validators condicionais baseados nos dados carregados ou inviteData
    const dataToValidate = inviteData || savedData;
    if (dataToValidate) {
      // Pega os valores atuais do formulário para validação
      const formValues = this.form.getRawValue();

      // Configura validators do document_type baseado na nacionality
      if (formValues.nacionality === '23') {
        this.form.controls.document_type.setValidators([]);
      } else {
        this.form.controls.document_type.setValidators([Validators.required]);
      }

      // Configura validators do document_name baseado no document_type
      if (formValues.document_type === 'other') {
        this.form.controls.document_name.setValidators([Validators.required]);
      } else {
        this.form.controls.document_name.setValidators([]);
      }
    }

    // Atualiza a validação de todos os controles após configurar validators
    if (dataToValidate) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].updateValueAndValidity({ emitEvent: false });
      });

      // Atualiza o status do formulário e força o statusChanges a ser disparado
      // Usa um pequeno delay para garantir que todos os validators estejam configurados
      setTimeout(() => {
        // Atualiza o formulário completo para disparar statusChanges
        this.form.updateValueAndValidity();

        // Garante que o status seja atualizado no signupService
        // Isso é necessário porque o statusChanges pode não ser disparado imediatamente
        const formStatus = this.form.status === 'VALID';
        this.signupService.setBasicFormStatus('personal', formStatus);
      }, 100);
    } else if (savedData) {
      // Se não houver dataToValidate mas houver savedData, também atualiza o status
      setTimeout(() => {
        this.form.updateValueAndValidity();
        const formStatus = this.form.status === 'VALID';
        this.signupService.setBasicFormStatus('personal', formStatus);
      }, 100);
    }

    this.form.valueChanges.subscribe(() => {
      // Usa getRawValue() para incluir campos desabilitados (como document quando vem do invite)
      const rawValues = this.form.getRawValue();
      // Normaliza o full_name e email antes de salvar
      const normalizedValues = {
        ...rawValues,
        full_name: SignupUnifiedPersonalComponent.normalizeFullName(
          rawValues.full_name
        ),
        email: SignupUnifiedPersonalComponent.normalizeEmail(rawValues.email),
      };
      this.signupService.setBasicFormData('personal', normalizedValues);
      // Salva no sessionStorage para persistência (limpa ao fechar a aba)
      this.storageService.setSession(
        'signup_basic_personal',
        normalizedValues,
        true
      );
    });

    // Converte email para minúsculas em tempo real
    this.form.controls.email.valueChanges.subscribe((value) => {
      if (value && value !== value.toLowerCase()) {
        this.form.controls.email.setValue(value.toLowerCase(), {
          emitEvent: false,
        });
      }
    });

    // Removido: o nome do título deve permanecer fixo com o valor do invite
    // Não atualiza this.name quando o campo full_name mudar

    this.form.controls.document_type.valueChanges.subscribe((value) => {
      if (value === 'other') {
        this.form.controls.document_name.setValidators([Validators.required]);
      } else {
        this.form.controls.document_name.setValidators([]);
      }
      this.form.controls.document_name.updateValueAndValidity();
    });

    this.form.controls.nacionality.valueChanges.subscribe((nacionality) => {
      if (nacionality === '23') {
        this.form.controls.document_type.setValidators([]);
      } else {
        this.form.controls.document_type.setValidators([Validators.required]);
      }

      this.form.controls.document_type.updateValueAndValidity();
    });

    // Revalida o telefone quando o DDI mudar
    this.form.controls.phone_ddi.valueChanges.subscribe(() => {
      this.form.controls.phone_number.updateValueAndValidity();
    });

    this.form.statusChanges.subscribe((status) => {
      if (status !== 'VALID') {
        Utils.getErrors(this.form);
      }

      this.signupService.setBasicFormStatus('personal', status === 'VALID');
    });

    // Desativa o loading após todos os dados serem carregados e aplicados
    this.loading = false;

    this.signupService.fillFormEvent.subscribe(() => {
      const randomFundID =
        this.fundOptions.length > 0
          ? this.fundOptions[
              Math.floor(Math.random() * this.fundOptions.length)
            ].value
          : null;

      let randomRoleIDs = [];

      if (this.userRole === 'provider') {
        randomRoleIDs = [
          this.agentOptions[
            Math.floor(Math.random() * this.agentOptions.length)
          ].value,
        ];
      }

      this.form.patchValue({
        agent: randomRoleIDs.length > 0 ? randomRoleIDs : null,
        full_name: fakerbr.name.findName(),
        email: fakerbr.internet.email(),
        nacionality: '23',
        document_type: 'CPF',
        document: this.haveBasicPFInfo
          ? this.form.controls.document.value
          : this.maskPipe.transform(fakerbr.br.cpf(), '000.000.000-00'),

        phone_ddi: '55',
        phone_number: this.maskPipe.transform(
          '139' +
            String(fakerbr.random.number({ min: 11111111, max: 99999999 })),
          '(00) 00000-0000'
        ),

        terms: true,
      });

      if (!this.haveFund) {
        this.form.patchValue({
          fund: randomFundID,
        });
      }
    });
  }

  backToLogin() {
    // Limpa os dados do personal do storage
    this.storageService.removeSession('signup_basic_personal');
    this.signupService.setBasicFormData('personal', null);

    // Se o convite já foi usado, vai direto para /login sem redirect
    this.router.navigateByUrl(`/login`);
  }

  renderText() {
    if (this.userRole === 'provider') {
      if (this.isPF) {
        return `seu perfil como ${this.role} na plataforma`;
      } else {
        return `sua empresa como ${this.role} junto`;
      }
    } else {
      if (this.isPF) {
        return `seu perfil como ${
          this.userRole === 'assignor' ? 'cedente' : 'cotista'
        } na plataforma`;
      } else {
        return `sua empresa como ${
          this.userRole === 'assignor' ? 'cedente' : 'cotista'
        } na plataforma`;
      }
    }
  }

  async getAgents() {
    try {
      const res = await this.api.get({
        route: 'api/registration/simplified-agent/',
      });

      this.agentOptions = res.map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
    } catch (error) {
      console.warn(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  async getFunds() {
    try {
      const res = await this.api.get({
        route: 'api/registration/fund',
        token: false,
      });

      const filteredArr = Utils.filterRepeatedValues(res.funds, 'id');

      const funds = filteredArr.map((fund: any) => {
        return {
          label: fund.name,
          value: fund.id,
        };
      });

      this.fundOptions = funds;

      // Se houver um fundo do invite que não está na lista, buscar e adicionar
      if (this.haveToken && this.route.snapshot.params.token) {
        try {
          const inviteRes = await this.api.get({
            route: `api/v3/registration/invite/public/`,
            params: { token: this.route.snapshot.params.token },
          });

          if (inviteRes && inviteRes.data) {
            // O fundo pode estar em inviteRes.data.data ou inviteRes.data
            const inviteData = inviteRes.data.data || inviteRes.data;
            const fundId = inviteData.fund;

            if (!fundId) return;
            const fundExists = this.fundOptions.some(
              (option) => option.value === fundId
            );

            if (!fundExists) {
              // Buscar o fundo específico na API funds/
              try {
                const fundRes = await this.api.get({
                  route: `funds/${fundId}/`,
                  token: false,
                });

                if (fundRes && fundRes.id) {
                  this.fundOptions.push({
                    label: fundRes.name,
                    value: fundRes.id,
                  });
                }
              } catch (fundError) {
                console.error('Erro ao buscar fundo específico:', fundError);
              }
            }
          }
        } catch (inviteError) {
          console.error('Erro ao buscar dados do invite:', inviteError);
        }
      }
    } catch (error) {
      this.toast.show('error', 'Erro!', 'Erro ao carregar fundos de interesse');
    }
  }

  handleKeyEnter(event: any) {
    event.preventDefault();
  }

  async initLoginComponent(
    newPaper: string | null,
    registerUuid: string | null
  ) {
    console.log('[SIGNUP-UNIFIED-PERSONAL] initLoginComponent chamado');
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] initLoginComponent - newPaper:',
      newPaper
    );
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] initLoginComponent - registerUuid:',
      registerUuid
    );

    // Mapear o tipo de papel para exibição
    const typeMap: { [key: string]: string } = {
      assignor: 'Cedente',
      shareholder: 'Cotista',
      provider: 'Prestador de Serviço',
    };
    this.loginTypeDisplay = newPaper ? typeMap[newPaper] || newPaper : '';
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] initLoginComponent - loginTypeDisplay:',
      this.loginTypeDisplay
    );

    // Buscar dados do convite para pegar o nome e fundo de interesse
    const token = this.route.snapshot.params.token;
    console.log('[SIGNUP-UNIFIED-PERSONAL] initLoginComponent - token:', token);

    if (token) {
      try {
        const res = await this.api.get({
          route: `api/v3/registration/invite/public/`,
          params: { token },
        });

        if (res.data) {
          const inviteData = res.data.data || res.data;

          // Pegar o nome do convite
          if (inviteData.name) {
            this.loginUserName = inviteData.name;
          }

          // Pegar person_type do convite (PF ou PJ)
          if (inviteData.person_type) {
            this.loginInvitePersonType = inviteData.person_type;
          } else if (inviteData.document) {
            // Inferir pelo tamanho do documento
            const docLength = inviteData.document.replace(/\D/g, '').length;
            this.loginInvitePersonType = docLength === 11 ? 'PF' : 'PJ';
          }

          // Pegar o papel do convite (se não vier do newPaper)
          if (res.data.type) {
            this.loginInviteRole = res.data.type;
          } else if (newPaper) {
            this.loginInviteRole = newPaper;
          }

          // Pegar o fundo de interesse se existir
          // Verificar se o fundo vem no próprio convite ou se precisa buscar
          if (inviteData.fund_interest_id) {
            // Tentar buscar o nome do fundo (pode falhar se não houver autenticação)
            // Mas não é crítico, apenas não mostrará o nome do fundo
            try {
              // Verificar se há token de autenticação (usuário pode já estar logado)
              const hasToken = this.authService.getToken();
              if (hasToken) {
                const fundRes = await this.api.get({
                  route: `api/v2/approvals/funds-dropdown/active/`,
                  token: true,
                });

                const funds = fundRes.data || [];
                const fund = funds.find(
                  (f: any) => f.value === inviteData.fund_interest_id
                );
                if (fund) {
                  this.loginFundName = fund.label;
                }
              }
            } catch (error) {
              // Não é crítico, apenas não mostrará o nome do fundo
              console.error('Erro ao buscar fundo de interesse:', error);
            }
          }

          // Verificar se o fundo vem diretamente no convite (res.data.fund ou similar)
          if (res.data.fund && res.data.fund.name) {
            this.loginFundName = res.data.fund.name;
          }
        }
      } catch (error) {
        console.error(
          '[SIGNUP-UNIFIED-PERSONAL] Erro ao buscar dados do convite:',
          error
        );
      }
    }

    console.log('[SIGNUP-UNIFIED-PERSONAL] initLoginComponent finalizado');
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] showLoginForm após initLoginComponent:',
      this.showLoginForm
    );
    console.log('[SIGNUP-UNIFIED-PERSONAL] loginUserName:', this.loginUserName);
    console.log(
      '[SIGNUP-UNIFIED-PERSONAL] loginTypeDisplay:',
      this.loginTypeDisplay
    );

    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  async validatePassword() {
    this.loginLoading = true;

    const value = Utils.onlyNumbers(this.loginForm.controls.username.value);
    const username = value ?? '';
    const password = this.loginPasswordForm.get('password')?.value ?? '';

    try {
      const { data } = await this.api.post<ApiResponse<NewLoginResponse>>({
        route: 'new-login/',
        body: { username, password },
      });

      if (!data.is_staff && !data.active_register) {
        this.toast.show(
          'error',
          'Erro',
          'O seu cadastro se encontra em uma versão antiga, por favor, entre em contato com o suporte para atualizar seu cadastro.'
        );
        this.loginLoading = false;
        this.loginForm.controls.username.setErrors({ incorrect: true });
        this.loginPasswordForm.controls.password.setErrors({ incorrect: true });
        return;
      }

      let permissionsArr =
        data.active_register.permissions_role &&
        data.active_register.permissions_role.permissions
          ? data.active_register.permissions_role.permissions
          : [];

      this.storageService.setPermissionType(data.type_permission);

      this.authService.setToken(data.tokens.access);
      this.authService.setUser(data);

      this.authService.permissions = permissionsArr;
      this.storageService.set('permissions', permissionsArr, true);

      // Verificar se o register ID do login é igual ao register_uuid da URL
      const loginRegisterId = data.active_register.register.id;
      const urlRegisterUuid = this.route.snapshot.queryParams['register_uuid'];

      // Se houver register_uuid na URL, verificar se é o mesmo do login
      if (urlRegisterUuid) {
        const urlRegisterId = urlRegisterUuid.toString();
        const loginRegisterIdStr = loginRegisterId.toString();

        if (urlRegisterId !== loginRegisterIdStr) {
          // Register IDs são diferentes - deslogar e mostrar erro
          await this.authService.clearUser();
          this.toast.show(
            'error',
            'Erro',
            'Você precisa fazer login com o mesmo usuário do convite para continuar o cadastro.'
          );
          this.loginLoading = false;
          return;
        }
      }

      // Register IDs são iguais ou não há register_uuid na URL - redirecionar para o cadastro
      const token = this.route.snapshot.params.token;
      if (token) {
        // Buscar dados do invite para adicionar role_draft e marcar como usado
        try {
          const inviteRes = await this.api.get({
            route: `api/v3/registration/invite/public/`,
            params: { token },
          });

          if (inviteRes?.data) {
            const inviteData = inviteRes.data.data || inviteRes.data;

            // Verificar se o convite já foi usado
            if (
              inviteRes.data.status === 'used' ||
              inviteData.status === 'used'
            ) {
              // Convite já foi usado - verificar se o papel já foi adicionado
              try {
                // Buscar dados atualizados do register
                const registerResponse = await this.api.get<any>({
                  route: `api/registration/register/${loginRegisterId}`,
                  token: true,
                });

                const register = registerResponse?.data;

                const targetRoleApplicable =
                  inviteRes.data.type ||
                  this.route.snapshot.queryParams['new_paper'] ||
                  this.loginInviteRole;

                // Verificar se o papel já existe no register
                const roleAlreadyExists =
                  register?.roles &&
                  Array.isArray(register.roles) &&
                  register.roles.some(
                    (r: any) =>
                      r &&
                      (r.applicable === targetRoleApplicable ||
                        r.id === inviteRes.data.invited_current_role?.id)
                  );

                if (roleAlreadyExists) {
                  // Papel já foi adicionado - redirecionar para página de detalhes
                  this.toast.show(
                    'info',
                    'Papel já adicionado',
                    'Este papel já foi adicionado ao seu cadastro.'
                  );
                  this.router.navigate([
                    `/app/details/${targetRoleApplicable}/${loginRegisterId}`,
                  ]);
                  this.loginLoading = false;
                  return;
                }
              } catch (error) {
                console.error('Erro ao verificar papel existente:', error);
                // Se não conseguir verificar, continua o fluxo normal
              }
            }

            // Verificar se o papel já existe no register antes de adicionar
            let shouldAddRole = true;
            try {
              // Buscar dados atualizados do register
              const registerResponse = await this.api.get<any>({
                route: `api/registration/register/${loginRegisterId}`,
                token: true,
              });

              const register = registerResponse?.data;

              const targetRoleApplicable =
                inviteRes.data.type ||
                this.route.snapshot.queryParams['new_paper'] ||
                this.loginInviteRole;

              // Verificar se o papel já existe no register
              const roleAlreadyExists =
                register?.roles &&
                Array.isArray(register.roles) &&
                register.roles.some(
                  (r: any) =>
                    r &&
                    (r.applicable === targetRoleApplicable ||
                      (inviteRes.data.invited_current_role &&
                        r.id === inviteRes.data.invited_current_role.id))
                );

              if (roleAlreadyExists) {
                shouldAddRole = false;
                // Papel já foi adicionado - redirecionar para página de detalhes
                this.toast.show(
                  'info',
                  'Papel já adicionado',
                  'Este papel já foi adicionado ao seu cadastro.'
                );
                this.router.navigate([
                  `/app/details/${targetRoleApplicable}/${loginRegisterId}`,
                ]);
                this.loginLoading = false;
                return;
              }
            } catch (error) {
              console.error('Erro ao verificar papel existente:', error);
              // Se não conseguir verificar, continua o fluxo normal
            }

            // Marcar o convite como usado antes de redirecionar (só se ainda não estiver usado)
            if (
              inviteRes.data.status !== 'used' &&
              inviteData.status !== 'used'
            ) {
              try {
                await this.api.patch({
                  route: `api/v3/registration/invite/public/`,
                  body: {
                    used: true,
                    status: 'used',
                    token: token,
                  },
                  token: true,
                });
              } catch (error) {
                console.error('Erro ao marcar convite como usado:', error);
                // Não bloqueia o redirecionamento mesmo se o PATCH falhar
              }
            }

            // Adicionar role ao register se houver invited_current_role e o papel ainda não existir
            if (inviteRes.data.invited_current_role && shouldAddRole) {
              try {
                await this.api.patch({
                  route: `api/v3/registration/registers/`,
                  body: {
                    register_uuid: loginRegisterId.toString(),
                    role_draft: inviteRes.data.invited_current_role,
                  },
                  token: true,
                });
              } catch (error) {
                console.error('Erro ao adicionar role ao register:', error);
                // Não bloqueia o redirecionamento mesmo se o PATCH falhar
              }

              const draft = inviteRes.data.invited_current_role as {
                id?: number;
              };
              const papersRoleId =
                draft && typeof draft === 'object' && draft.id != null
                  ? Number(draft.id)
                  : null;
              if (papersRoleId != null && !Number.isNaN(papersRoleId)) {
                try {
                  await this.api.post({
                    route: `api/v2/registration/registers/${loginRegisterId.toString()}/papers/`,
                    token: true,
                    body: { role_id: papersRoleId },
                  });
                } catch (papersErr) {
                  console.error(
                    'Erro ao registrar papers após role_draft:',
                    papersErr,
                  );
                  this.toast.show(
                    'warning',
                    'Atenção',
                    'Fluxo iniciado, mas houve falha ao registrar o papel. Tente novamente ou contate o suporte.',
                  );
                }
              }
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados do invite:', error);
          // Não bloqueia o redirecionamento mesmo se a busca do invite falhar
        }

        // Determinar o papel (role)
        const role =
          this.loginInviteRole ||
          this.route.snapshot.queryParams['new_paper'] ||
          data.active_register.register.role?.applicable;

        // Determinar o tipo (PF ou PJ) - converter para minúsculas
        const personType = (
          this.loginInvitePersonType ||
          data.active_register.register.type ||
          'PF'
        ).toLowerCase();

        // Fazer PATCH no register-step para definir como 'register' e 'general'
        // Isso evita que o componente redirecione para dashboard quando fizer o GET
        try {
          if (personType === 'pf') {
            await this.signupService.updateUserStepPF(
              'register',
              'general',
              loginRegisterId.toString()
            );
          } else {
            await this.signupService.updateUserStepCompany(
              'register',
              'general',
              loginRegisterId.toString()
            );
          }
        } catch (error) {
          console.error('Erro ao atualizar register-step:', error);
          // Não bloqueia o redirecionamento mesmo se o PATCH falhar
        }

        // Usar o register ID do login como UUID
        const registerId = loginRegisterId.toString();

        // Redirecionar para o cadastro com os parâmetros new_paper e register_uuid
        this.router.navigate([`/signup/${role}/${personType}/${registerId}`], {
          queryParams: {
            new_paper: this.route.snapshot.queryParams['new_paper'],
            register_uuid: urlRegisterUuid || registerId,
          },
        });
      } else {
        // Se não houver token, redirecionar para dashboard
        this.router.navigate(['/app/dashboard']);
      }
    } catch (error: any) {
      console.warn(error);

      if (error.error && error.error.detail) {
        if (error.error.detail.includes('verified')) {
          this.storageService.setPreUser({ username: username });
          this.toast.show(
            'error',
            'Erro',
            'Sua conta precisa ser verificada. Entre em contato com o suporte.'
          );
        } else {
          this.toast.show('error', 'Erro!', 'Senha incorreta');
          this.loginPasswordForm.controls.password.setErrors({
            incorrect: true,
          });
        }
      } else {
        this.toast.show('error', 'Erro!', 'Senha incorreta');
        this.loginPasswordForm.controls.password.setErrors({ incorrect: true });
      }
    }

    this.loginLoading = false;
  }

  canProceedLogin(): boolean {
    return this.loginForm.valid && this.loginPasswordForm.valid;
  }
}
